import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TablePager } from "../../components/shared/TablePager";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import { AdminGate } from "../../components/admin/AdminGate";
import { useToast } from "../../components/toast/toastContext";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../session/SessionContext";
import { impersonateUser } from "../../api/session";
import { reloadPortal } from "../../components/toast/pendingToast";
import { PersonProviderAccessSection } from "../../components/admin/PersonProviderAccessSection";
import { PersonProviderAllocationPanel } from "../../components/admin/PersonProviderAllocationPanel";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import styles from "../../components/admin/Admin.module.css";
import {
  classifyPerson,
  fetchAllPeople,
  fetchAssociationCatalogue,
  fetchPersonAssociations,
  fetchPersonGroups,
  setPersonDisplayName,
  setPersonAssociation,
  type PersonAssociations,
  type PersonGroup,
  type PersonListPage,
  type PersonSummary,
} from "../../api/entitlementAdmin";
import { formatAge } from '../../components/consumption/formatters';
import { formatPersonName } from "../../components/shared/personDisplay";
import { confirmEntitlementMembership, previewEntitlementMembership, type GroupImpactPreview } from "../../api/entitlementGroupsAdmin";
import { Dropdown } from "../../components/shared/Dropdown";
import { useColumnFilters, type ColumnFilterSpec } from "../../components/shared/columnFilters";
import { Tooltip } from "../../components/shared/Tooltip";

const AdministerPermission = "entitlement.administer";
const ImpersonatePermission = "identity.impersonate";
const peopleSearchStorageKey = "admin-users-search";

/** The columns the people table can be filtered on: names by what they contain, the rest by their values. */
const peopleFilters: readonly ColumnFilterSpec<PersonSummary>[] = [
  { id: "email", kind: "text", value: (person) => person.email },
  { id: "displayName", kind: "text", value: (person) => person.displayName },
  { id: "division", kind: "set", value: (person) => person.division ?? "" },
  { id: "department", kind: "set", value: (person) => person.department ?? "" },
  { id: "status", kind: "set", value: (person) => person.status },
];

/**
 * The user management screen (phase 21, `AIA-81`): browse people and edit one at a time in a
 * dialog — the classification action and the person association and set-distribution actions,
 * everything phase 15's `EntitlementAdminEndpoints` already exposes as an API, gated throughout on
 * `entitlement.administer`. Deliberately keyed by raw person id rather than a name-search box for
 * the edit actions themselves — this phase does not add a lookup endpoint beyond the browse list,
 * and it is not this screen's job to invent one. Distribution options, allowance groups and seat
 * costs live on the Providers screen (`ProvidersPage`) — they are organisation-wide
 * configuration, not something scoped to one person.
 */
export function EntitlementAdminPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={styles.usersPage}>
      <AdminGate permissions={[AdministerPermission]} title={t("admin.entitlement.denied.title")} description={t("admin.entitlement.denied.description")}>
        <PendingPeopleSection onEdit={(person) => void navigate(`/admin/users/${encodeURIComponent(person.id)}`)} />
      </AdminGate>
    </div>
  );
}

/**
 * The browse list `/admin/entitlement` had no answer for until now (`PHASE-11C`, `AIA-81`):
 * every person-action endpoint below is keyed by a raw id, with no way to discover one short of
 * already knowing it. Defaults to `Pending` — the status every bootstrap-created and newly
 * auto-created person starts in — since that is the queue an administrator actually works from.
 */
function PendingPeopleSection({ onEdit }: { onEdit: (person: PersonSummary) => void }): React.JSX.Element {
  const { t } = useTranslation();
  const [page, setPage] = useState<PersonListPage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(() => window.localStorage.getItem(peopleSearchStorageKey) ?? "");

  useEffect(() => {
    window.localStorage.setItem(peopleSearchStorageKey, searchTerm);
  }, [searchTerm]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const result = await fetchAllPeople();
    if (result.kind === "ok") {
      setPage(result.response);
    } else if (result.kind === "denied") {
      setError(t("admin.entitlement.denied.description"));
    } else {
      setError(result.message);
    }

    setIsLoading(false);
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredPeople = useMemo(() => {
    const term = searchTerm.trim().toLocaleLowerCase();
    if (page === null || term.length === 0) {
      return page?.items ?? [];
    }

    return page.items.filter((person) => person.email.toLocaleLowerCase().includes(term) || person.displayName.toLocaleLowerCase().includes(term));
  }, [page, searchTerm]);

  const { filtered: shownPeople, heading } = useColumnFilters(filteredPeople, peopleFilters);

  const columns: TableColumn<PersonSummary>[] = [
    { id: "email", name: heading("email", t("admin.entitlement.people.email")), selector: (person) => person.email, sortable: true, wrap: true, grow: 2 },
    { id: "displayName", name: heading("displayName", t("admin.entitlement.people.displayName")), selector: (person) => person.displayName, cell: (person) => formatPersonName(person) === person.email ? "—" : person.displayName, sortable: true, wrap: true, grow: 2 },
    { id: "division", name: heading("division", t("admin.entitlement.people.division")), selector: (person) => person.division ?? "", sortable: true, wrap: true },
    { id: "department", name: heading("department", t("admin.entitlement.people.department")), selector: (person) => person.department ?? "", sortable: true, wrap: true },
    { id: "status", name: heading("status", t("admin.entitlement.people.status")), selector: (person) => person.status, sortable: true },
    { id: "createdAt", name: t("admin.entitlement.people.createdAt"), selector: (person) => person.createdAt, sortable: true, format: (person) => t("admin.entitlement.people.createdAgo", { age: formatAge(person.createdAt) }) },
    { id: "actions", name: <span className="visually-hidden">{t("dataTable.actionsColumn")}</span>, button: true, ignoreRowClick: true, cell: (person) => <button type="button" className="button button-secondary" onClick={() => onEdit(person)}>{t("admin.entitlement.people.edit")}</button> },
  ];

  return (
    <section className={`${styles.section} ${styles.usersSection}`}>

      {/* The full-page loading/error states only ever apply before the first page has ever loaded. */}
      {page === null && isLoading && <LoadingState label={t("admin.entitlement.people.loading")} />}
      {page === null && !isLoading && error && (
        <ErrorState title={t("admin.entitlement.people.title")} description={error} onRetry={() => void load()} />
      )}
      {page !== null && (
        <>
          {page.items.length === 0 ? (
            <EmptyState title={t("admin.entitlement.people.title")} description={t("admin.entitlement.people.empty")} />
          ) : (
            <>
            <div className={styles.tableToolbar}>
              <input
                aria-label={t("admin.entitlement.people.searchLabel")}
                className={styles.peopleSearch}
                type="search"
                placeholder={t("admin.entitlement.people.searchPlaceholder")}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className={`${styles.tableWrapper} ${styles.peopleTableWrapper}`} aria-busy={isLoading}>
              <DataTable
                persistTableHead
                columns={columns}
                data={shownPeople}
                className={styles.peopleDataTable}
                keyField="id"
                ariaLabel={t("admin.entitlement.people.title")}
                theme={adminTableTheme}
                striped
                highlightOnHover
                columnSeparator="subtle"
                pagination
                paginationComponent={TablePager}
                paginationPerPage={25}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                defaultSortFieldId="createdAt"
                defaultSortAsc={false}
                progressPending={isLoading}
                progressComponent={<LoadingState label={t("admin.entitlement.people.loading")} />}
                fixedHeader
                fixedHeaderScrollHeight="100%"
              />
            </div>
            {/* A refresh that fails (e.g. the second page onward) keeps the last-good rows on
                screen rather than wiping them — this note is the only thing that changes. */}
            {error && (
              <p className={`${styles.feedback} ${styles.feedbackError}`} role="alert">
                {error}
              </p>
            )}
            </>
          )}
        </>
      )}
    </section>
  );
}

/** Rendered by the person detail page, which supplies the heading. */
export function PersonActionsSection({ person, onDisplayNameSaved, onAllocationSaved }: { person: PersonSummary; onDisplayNameSaved: (displayName: string) => void; onAllocationSaved?: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { snapshot } = useSession();
  const [isImpersonating, setIsImpersonating] = useState(false);
  // No nesting: while this session is itself impersonating someone, the switch is not offered.
  const canImpersonate = snapshot.status === "signed-in" && snapshot.permissions.includes(ImpersonatePermission) && !snapshot.impersonated;
  const [displayName, setDisplayName] = useState(person.displayName);
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [displayNameDraft, setDisplayNameDraft] = useState(person.displayName);
  const [isSavingDisplayName, setIsSavingDisplayName] = useState(false);
  // The status an administrator may classify *to* depends on the status recorded *now* — updated
  // as soon as a classification succeeds, so a further change in the same dialog session
  // immediately re-evaluates which transitions are still valid.
  const [currentStatus, setCurrentStatus] = useState(person.status);
  const [associations, setAssociations] = useState<{ kind: "loading" } | { kind: "loaded"; data: PersonAssociations } | { kind: "error"; message: string }>({
    kind: "loading",
  });

  useEffect(() => {
    let cancelled = false;
    setAssociations({ kind: "loading" });
    void (async () => {
      const result = await fetchPersonAssociations(person.id);
      if (cancelled) {
        return;
      }

      if (result.kind === "ok") {
        setAssociations({ kind: "loaded", data: result.response });
      } else if (result.kind === "denied") {
        setAssociations({ kind: "error", message: t("admin.denied.description", { permissions: AdministerPermission }) });
      } else {
        setAssociations({ kind: "error", message: result.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [person.id, t]);

  const handleDisplayNameSave = async () => {
    const name = displayNameDraft.trim();
    if (!name) {
      toast.show(t("admin.entitlement.person.displayNameRequired"), "error");
      return;
    }

    setIsSavingDisplayName(true);
    const result = await setPersonDisplayName(person.id, name);
    setIsSavingDisplayName(false);
    if (result.kind === "ok") {
      setDisplayName(result.response.displayName);
      setDisplayNameDraft(result.response.displayName);
      onDisplayNameSaved(result.response.displayName);
      setEditingDisplayName(false);
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: AdministerPermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  // One click switches this browser session to the person on screen, as Header's impersonation dialog does.
  const handleImpersonate = async () => {
    setIsImpersonating(true);
    const result = await impersonateUser(person.id);
    setIsImpersonating(false);
    if (result.kind === "ok") {
      // A full reload, so that every page re-reads the session as the new identity.
      reloadPortal();
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: ImpersonatePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const handleStatusChange = async (status: string) => {
    const result = await classifyPerson(person.id, status);
    if (result.kind === "ok") {
      setCurrentStatus(result.response.newStatus);
      toast.show(t("admin.entitlement.person.classifySuccess", { status: result.response.newStatus }), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: AdministerPermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  // A person already Active or Inactive cannot be sent back to Pending — that transition only ever
  // happens once, at bootstrap. `Pending` only appears as an option while it is still the current
  // status, so the dropdown always includes whatever is currently selected.
  const statusOptions = currentStatus === "Pending" ? ["Pending", "Active", "Inactive"] : ["Active", "Inactive"];

  return (
    <div className={styles.personEditLayout}>
      <div className={styles.personDetailsBox}>
      {canImpersonate && (
        <div className={styles.personDetailsToolbar}>
          <button type="button" className="button button-secondary" onClick={() => void handleImpersonate()} disabled={isImpersonating}>
            {t("session.impersonate")}
          </button>
        </div>
      )}
      <div className={styles.personDetailsGrid}>
      <div>
      <div className={styles.personIdentity}>
        <div className={styles.personIdentityRow}>
          <span className={styles.note}>{t("admin.entitlement.people.email")}:</span>
          <strong>{person.email}</strong>
        </div>
        <div className={styles.personIdentityRow}>
          <span className={styles.note}>{t("admin.entitlement.people.displayName")}:</span>
          {editingDisplayName ? (
            <div className={styles.displayNameEditor}>
              <input
                aria-label={t("admin.entitlement.people.displayName")}
                autoFocus
                value={displayNameDraft}
                onChange={(event) => setDisplayNameDraft(event.target.value)}
                onBlur={() => void handleDisplayNameSave()}
                disabled={isSavingDisplayName}
              />
            </div>
          ) : (
            <button type="button" className={styles.editableDisplayName} onClick={() => { setDisplayNameDraft(displayName); setEditingDisplayName(true); }}>
              <strong>{displayName.trim().length === 0 || formatPersonName({ ...person, displayName }) === person.email ? "—" : displayName}</strong>
            </button>
          )}
        </div>
      </div>

      <div className={styles.form}>
        <div className={`${styles.field} ${styles.userEditSelectField}`}>
          <label htmlFor="classify-status">{t("admin.entitlement.person.status")}</label>
          <Dropdown
            id="classify-status"
            options={statusOptions.map((option) => ({
              value: option,
              label: t(`admin.entitlement.person.statusOptions.${option.toLowerCase()}`),
            }))}
            value={currentStatus}
            onChange={(status) => void handleStatusChange(status)}
          />
        </div>
      </div>

      {associations.kind === "loading" && <LoadingState label={t("admin.entitlement.person.associationsLoading")} />}
      {associations.kind === "error" && <ErrorState description={associations.message} />}
      {associations.kind === "loaded" && (
        <>
          <DivisionDepartmentField personId={person.id} currentDivision={associations.data.division} currentDepartment={associations.data.department} />
        </>
      )}

      </div>
      <div>
      <PersonGroupsField personId={person.id} />
      </div>
      </div>
      </div>
      <div className={styles.personAllocationColumn}>
        <PersonProviderAllocationPanel personId={person.id} onSaved={onAllocationSaved} />
        <PersonProviderAccessSection personId={person.id} />
      </div>
    </div>
  );
}

/**
 * A person's organisational unit as one combined "Division / Department" dropdown. Every Department has a
 * single parent Division, so choosing an entry saves both associations. Selecting saves immediately.
 */
function DivisionDepartmentField({
  personId,
  currentDivision,
  currentDepartment,
}: {
  personId: string;
  currentDivision: string | null;
  currentDepartment: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [entries, setEntries] = useState<{ division: string; department: string }[]>([]);
  const currentKey = currentDivision && currentDepartment ? `${currentDivision} / ${currentDepartment}` : "";
  const [selected, setSelected] = useState(currentKey);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [divisions, departments] = await Promise.all([fetchAssociationCatalogue("Division"), fetchAssociationCatalogue("Department")]);
      if (cancelled || divisions.kind !== "ok" || departments.kind !== "ok") {
        return;
      }

      const divisionNames = new Map(divisions.response.map((division) => [division.id, division.value]));
      const combined = departments.response
        .filter((department) => department.parentId != null && divisionNames.has(department.parentId))
        .map((department) => ({ division: divisionNames.get(department.parentId!)!, department: department.value }))
        .sort((left, right) => `${left.division} / ${left.department}`.localeCompare(`${right.division} / ${right.department}`));
      setEntries(combined);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSelected(currentKey);
  }, [currentKey]);

  const save = async (key: string) => {
    const entry = entries.find((candidate) => `${candidate.division} / ${candidate.department}` === key);
    if (!entry) {
      return;
    }

    // The division comes from the department, so saving the department is enough.
    const result = await setPersonAssociation(personId, "Department", entry.department);
    if (result.kind === "ok") {
      toast.show(t("admin.entitlement.person.associationSuccess"), "success");
      setSelected(key);
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: AdministerPermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const showCurrentFallback = currentKey !== "" && !entries.some((entry) => `${entry.division} / ${entry.department}` === currentKey);

  return (
    <div className={styles.form}>
      <div className={`${styles.field} ${styles.userEditSelectField}`}>
        <label htmlFor="association-division-department">{t("admin.entitlement.person.divisionDepartmentLabel")}</label>
        <Dropdown
          id="association-division-department"
          options={[
            { value: "", label: t("admin.entitlement.person.associationValues.choose"), disabled: true },
            ...(showCurrentFallback ? [{ value: currentKey, label: currentKey }] : []),
            ...entries.map((entry) => {
              const key = `${entry.division} / ${entry.department}`;
              return { value: key, label: key };
            }),
          ]}
          value={selected}
          onChange={(next) => void save(next)}
        />
      </div>
    </div>
  );
}

function PersonGroupsField({ personId }: { personId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; groups: PersonGroup[] } | { kind: "error"; message: string }>({ kind: "loading" });
  const [busyGroupId, setBusyGroupId] = useState<string | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<{ group: { id: string; name: string }; preview: GroupImpactPreview } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await fetchPersonGroups(personId);
      if (cancelled) {
        return;
      }

      if (result.kind === "ok") {
        setState({ kind: "loaded", groups: result.response });
      } else {
        setState({ kind: "error", message: result.kind === "denied" ? t("admin.denied.description", { permissions: AdministerPermission }) : result.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId, t]);

  const removeGroup = async (group: { id: string; name: string }) => {
    setBusyGroupId(group.id);
    const preview = await previewEntitlementMembership(group.id, personId, false);
    if (preview.kind !== "ok") {
      toast.show(preview.kind === "denied" ? t("groupAdmin.denied") : preview.message, "error");
      setBusyGroupId(null);
      return;
    }

    setPendingRemoval({ group, preview: preview.response });
    setBusyGroupId(null);
  };

  const confirmRemoveGroup = async () => {
    if (pendingRemoval === null) {
      return;
    }

    const { group, preview } = pendingRemoval;
    setBusyGroupId(group.id);
    const result = await confirmEntitlementMembership(group.id, personId, false, preview.revisionSnapshot);
    if (result.kind === "ok") {
      setState((current) => current.kind === "loaded" ? { ...current, groups: current.groups.filter((item) => item.id !== group.id) } : current);
    } else {
      toast.show(result.kind === "denied" ? t("groupAdmin.denied") : result.message, "error");
    }

    setPendingRemoval(null);
    setBusyGroupId(null);
  };

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.entitlement.person.groupsLoading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState description={state.message} />;
  }

  return (
    <div className={`${styles.field} ${styles.userEditSelectField} ${styles.personGroupsField}`}>
      <span className={styles.note}>{t("admin.entitlement.person.groupLabel")}</span>
      {state.groups.length > 0 ? (
        <ul className={styles.personGroupList}>
          {state.groups.map((group) => (
            <li key={group.id}>
              <span>{group.name}</span>
              {group.providesAllowance && <Tooltip label={t("admin.entitlement.person.allowanceSource")}><span className={styles.allowanceSourceMarker} role="img" aria-label={t("admin.entitlement.person.allowanceSource")}>$</span></Tooltip>}
              <Tooltip label={t("admin.entitlement.person.removeGroup", { group: group.name })}>
                <button
                  type="button"
                  className={styles.removePersonGroupButton}
                  aria-label={t("admin.entitlement.person.removeGroup", { group: group.name })}
                  disabled={busyGroupId !== null || pendingRemoval !== null}
                  onClick={() => void removeGroup(group)}
                >
                  ×
                </button>
              </Tooltip>
            </li>
          ))}
        </ul>
      ) : (
        <span>—</span>
      )}
      {pendingRemoval && (
        <section className={styles.personGroupConfirmation} aria-labelledby="remove-group-confirmation-title">
          <h3 id="remove-group-confirmation-title">{t("admin.entitlement.person.removeGroupConfirmationTitle")}</h3>
          <p>{t("admin.entitlement.person.removeGroupConfirm", { group: pendingRemoval.group.name, count: pendingRemoval.preview.users.length })}</p>
          <div className={styles.personGroupConfirmationActions}>
            <button type="button" className="button button-primary" onClick={() => void confirmRemoveGroup()} disabled={busyGroupId !== null}>
              {t("admin.entitlement.person.confirmGroupRemoval")}
            </button>
            <button type="button" className="button button-secondary" onClick={() => setPendingRemoval(null)} disabled={busyGroupId !== null}>
              {t("admin.entitlement.person.cancelGroupRemoval")}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
